/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import {
  DataMaskStateWithId,
  JsonObject,
  PartialFilters,
} from '@superset-ui/core';
import getFormDataWithExtraFilters from 'src/dashboard/util/charts/getFormDataWithExtraFilters';
import { getAppliedFilterValues } from 'src/dashboard/util/activeDashboardFilters';
import { ChartConfiguration, ChartQueryPayload } from 'src/dashboard/types';

/**
 * Redux state slice required to build dashboard chart query formData.
 * Kept structural to avoid coupling util to the full RootState type.
 */
export type DashboardChartFormDataState = {
  charts: Record<string | number, { form_data?: JsonObject }>;
  dashboardInfo: {
    id: number;
    metadata?: {
      chart_configuration?: JsonObject;
      label_colors?: Record<string, string>;
      shared_label_colors?: Record<string, string>;
      cache_timeout?: number | null;
    };
  };
  dashboardState: {
    colorScheme?: string;
    colorNamespace?: string;
    sliceIds: number[];
  };
  dataMask: Record<string | number, { ownState?: JsonObject }>;
  nativeFilters?: { filters?: JsonObject };
  sliceEntities?: {
    slices?: Record<number, { slice_id?: number }>;
  };
};

/**
 * Build query formData for a dashboard chart, including native filters and dataMask.
 * Used when refreshing charts that have never loaded (no latestQueryFormData yet).
 */
export function buildDashboardChartFormData(
  chartId: number,
  state: DashboardChartFormDataState,
): JsonObject | null {
  const {
    charts: chartQueries,
    dashboardInfo,
    dashboardState,
    dataMask,
    nativeFilters,
    sliceEntities,
  } = state;

  const chart = chartQueries[chartId];
  const slice = sliceEntities?.slices?.[chartId];
  if (!chart?.form_data || !slice) {
    return null;
  }

  const { colorScheme, colorNamespace } = dashboardState;
  const labelColors = dashboardInfo?.metadata?.label_colors || {};
  const sharedLabelColors = dashboardInfo?.metadata?.shared_label_colors || {};

  const formData = getFormDataWithExtraFilters({
    chart: { id: chartId, ...chart } as ChartQueryPayload,
    chartConfiguration: (dashboardInfo.metadata?.chart_configuration ??
      {}) as ChartConfiguration,
    filters: getAppliedFilterValues(chartId),
    colorScheme,
    colorNamespace,
    sliceId: chartId,
    nativeFilters: (nativeFilters?.filters ?? {}) as PartialFilters,
    allSliceIds: dashboardState.sliceIds,
    dataMask: dataMask as DataMaskStateWithId,
    extraControls: {},
    labelColors,
    sharedLabelColors,
    dashboardCacheTimeout: dashboardInfo?.metadata?.cache_timeout,
  });

  formData.dashboardId = dashboardInfo.id;
  return formData;
}
